import { FormControl, InputLabel, MenuItem, Select, SelectProps } from "@mui/material";
import { IGWidget, appendClass } from "../GWidget";
import _, { values } from "lodash";
import React, { forwardRef, MutableRefObject, Ref, useEffect, useImperativeHandle, useRef, useState } from "react";

export type TSelectValue = string|number;
export type TSelectEvent<T extends TSelectValue> = (selected:T|null) => void;
export interface ISelectRow<T extends TSelectValue> {
    label:string;
    value:T;
}
export type TRowRenderer<T extends TSelectValue> = (row:ISelectRow<T>) => React.ReactElement
export interface IDropdownProps<T extends TSelectValue> extends IGWidget {
    value?:T;
    defaultValue?:T;
    error?:string;
    options:ISelectRow<T>[]
    label?:string;
    onChange?:TSelectEvent<T>;
    size?:"small"|"medium"
    inputRef?:MutableRefObject<any>
    rowRenderer?:TRowRenderer<T>
}
export function getRow<T extends TSelectValue>(options:ISelectRow<T>[], value:T|undefined) : ISelectRow<T> | null {
    if (value!==undefined) return _.find(options, o => o.value===value) || null
    else return null
}
let g_selectId = 0;
function _Dropdown<T extends TSelectValue>(props:IDropdownProps<T>, ref:any) {
    const [open,setOpen] = useState<boolean>(false)
    const [value,setValue] = useState<T|undefined>(props.defaultValue === undefined ? props.value : props.defaultValue)
    const inputRef = props.inputRef || useRef<any>()
    const mounted = useRef<boolean>(false)

    const rows = props.options.map(item => {
        const element = props.rowRenderer ? props.rowRenderer(item) : item.label
        return <MenuItem key={item.value} value={item.value}>{element}</MenuItem>
    })
    const id = `DDL_${g_selectId++}`
    const label = props.label ? <InputLabel id={id}>{props.label}</InputLabel> : null
    const selectProps:SelectProps<T> = {labelId:id,inputRef}

    selectProps.size = props?.size || "small"
    selectProps.value = value
    selectProps.renderValue = v => props.options.find(o => o.value==v)?.label || ""
    selectProps.onChange = (e:any) => {
        setValue(e.target.value)
        props.onChange?.(e.target.value)
    }

    useImperativeHandle(ref, () => ({
        close:() => setOpen(false),
        get value() { return inputRef.current?.value }
    }), [])
    
    useEffect(() => {
        if (mounted.current) setValue(props.value)
        else mounted.current = true
    }, [props.value])

    const onOpen = () => setOpen(true)
    const onClose = () => setOpen(false)
    return <FormControl className={appendClass("dropdown", props.className)} id={props.id}>{label}<Select {...selectProps} open={open} onOpen={onOpen} onClose={onClose}>{rows}</Select></FormControl>
}

export default forwardRef(_Dropdown) as <T extends TSelectValue>(props:IDropdownProps<T>&{ref?:any}) => JSX.Element